// Hand-maintained — PipelineExecution HITL checkpoint management

import type { RequestOptions } from "../base-client";
import type { RequestBuilder } from "../request-builder";

/** Status of a pipeline execution. */
export type PipelineExecutionStatus =
  | "running"
  | "awaiting_approval"
  | "approved"
  | "rejected"
  | "completed"
  | "failed"
  | "expired";

/** A pipeline execution resource. */
export interface PipelineExecution {
  id: string;
  pipeline_id: string;
  workspace_id: string | null;
  status: PipelineExecutionStatus;
  input: Record<string, unknown>;
  node_outputs: Record<string, unknown>;
  checkpoint_node: string | null;
  checkpoint_data: Record<string, unknown> | null;
  checkpoint_notes: string | null;
  checkpoint_refinements: Array<{
    node_id: string;
    instruction: string;
    timestamp: string;
  }>;
  checkpoint_session_id: string | null;
  started_at: string;
  completed_at: string | null;
}

/** Attributes for editing checkpoint data before approval. */
export interface UpdateCheckpointAttributes {
  /** RD-edited output data. Corrected values flow into downstream nodes. */
  checkpoint_data?: Record<string, unknown>;
}

/** Attributes for requesting AI refinement of a checkpoint node. */
export interface AiRefineAttributes {
  /** The node ID to re-run with the instruction. */
  node_id: string;
  /** Natural language instruction for the refinement. */
  instruction: string;
}

// Named request functions — match pipelines.ts convention
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAdminPipelineExecutionsById = (options: Record<string, any>) =>
  (options.client ?? null).get({
    url: "/admin/pipeline-executions/{id}",
    ...options,
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAdminPipelineExecutions = (options: Record<string, any>) =>
  (options.client ?? null).get({
    url: "/admin/pipeline-executions",
    ...options,
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const patchAdminPipelineExecutionsByIdApprove = (
  options: Record<string, any>,
) =>
  (options.client ?? null).patch({
    url: "/admin/pipeline-executions/{id}/approve",
    ...options,
    headers: { "Content-Type": "application/vnd.api+json", ...options.headers },
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const patchAdminPipelineExecutionsByIdReject = (options: Record<string, any>) =>
  (options.client ?? null).patch({
    url: "/admin/pipeline-executions/{id}/reject",
    ...options,
    headers: { "Content-Type": "application/vnd.api+json", ...options.headers },
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const patchAdminPipelineExecutionsByIdCheckpoint = (
  options: Record<string, any>,
) =>
  (options.client ?? null).patch({
    url: "/admin/pipeline-executions/{id}/checkpoint",
    ...options,
    headers: { "Content-Type": "application/vnd.api+json", ...options.headers },
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const patchAdminPipelineExecutionsByIdNote = (options: Record<string, any>) =>
  (options.client ?? null).patch({
    url: "/admin/pipeline-executions/{id}/note",
    ...options,
    headers: { "Content-Type": "application/vnd.api+json", ...options.headers },
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const patchAdminPipelineExecutionsByIdRefine = (options: Record<string, any>) =>
  (options.client ?? null).patch({
    url: "/admin/pipeline-executions/{id}/refine",
    ...options,
    headers: { "Content-Type": "application/vnd.api+json", ...options.headers },
  });

/**
 * Creates the pipelineExecutions namespace for managing HITL pipeline checkpoints.
 *
 * @param rb - The request builder used for API communication.
 * @returns Pipeline executions namespace.
 *
 * @example
 * ```typescript
 * // Poll status after triggering
 * const execution = await admin.pipelineExecutions.get(executionId);
 * if (execution.status === "awaiting_approval") {
 *   await admin.pipelineExecutions.updateCheckpoint(executionId, {
 *     checkpoint_data: { ...correctedValues },
 *   });
 *   await admin.pipelineExecutions.addNote(executionId, "Corrected HbA1c from 7.8 to 7.2");
 *   await admin.pipelineExecutions.approve(executionId);
 * }
 * ```
 */
export function createPipelineExecutionsNamespace(rb: RequestBuilder) {
  return {
    /**
     * Get a pipeline execution by ID.
     *
     * @param id - Execution UUID.
     * @param options - Optional request options.
     * @returns PipelineExecution resource.
     */
    async get(id: string, options?: RequestOptions) {
      return rb.execute(
        getAdminPipelineExecutionsById,
        { path: { id } },
        options,
      );
    },

    /**
     * List pipeline executions for a workspace.
     *
     * @param workspaceId - Workspace UUID to filter by.
     * @param filters - Optional additional filters (status).
     * @param options - Optional request options.
     * @returns Array of PipelineExecution resources.
     */
    async list(
      workspaceId: string,
      filters?: { status?: PipelineExecutionStatus },
      options?: RequestOptions,
    ) {
      return rb.execute(
        getAdminPipelineExecutions,
        { query: { workspace_id: workspaceId, ...filters } },
        options,
      );
    },

    /**
     * Approve the current checkpoint. Pipeline resumes with current checkpoint_data.
     * Only valid when status === "awaiting_approval".
     *
     * @param id - Execution UUID.
     * @param options - Optional request options.
     */
    async approve(id: string, options?: RequestOptions) {
      return rb.execute(
        patchAdminPipelineExecutionsByIdApprove,
        {
          path: { id },
          body: {
            data: { type: "pipeline-execution", id, attributes: {} },
          },
        },
        options,
      );
    },

    /**
     * Reject the current checkpoint. Pipeline halts — no downstream nodes run.
     * Only valid when status === "awaiting_approval".
     *
     * @param id - Execution UUID.
     * @param options - Optional request options.
     */
    async reject(id: string, options?: RequestOptions) {
      return rb.execute(
        patchAdminPipelineExecutionsByIdReject,
        {
          path: { id },
          body: {
            data: { type: "pipeline-execution", id, attributes: {} },
          },
        },
        options,
      );
    },

    /**
     * Edit checkpoint_data before approving.
     * Only valid when status === "awaiting_approval".
     * Corrected values flow into all downstream pipeline nodes.
     *
     * @param id - Execution UUID.
     * @param attributes - Fields to update.
     * @param options - Optional request options.
     */
    async updateCheckpoint(
      id: string,
      attributes: UpdateCheckpointAttributes,
      options?: RequestOptions,
    ) {
      return rb.execute(
        patchAdminPipelineExecutionsByIdCheckpoint,
        {
          path: { id },
          body: {
            data: { type: "pipeline-execution", id, attributes },
          },
        },
        options,
      );
    },

    /**
     * Set the checkpoint note. Replaces any existing note.
     * Only valid when status === "awaiting_approval".
     *
     * @param id - Execution UUID.
     * @param note - Note text.
     * @param options - Optional request options.
     */
    async addNote(id: string, note: string, options?: RequestOptions) {
      return rb.execute(
        patchAdminPipelineExecutionsByIdNote,
        {
          path: { id },
          body: {
            data: {
              type: "pipeline-execution",
              id,
              attributes: { checkpoint_notes: note },
            },
          },
        },
        options,
      );
    },

    /**
     * Request AI refinement of a specific checkpoint node.
     * Only valid when status === "awaiting_approval".
     *
     * @param id - Execution UUID.
     * @param nodeId - The node ID to re-run with the instruction.
     * @param instruction - Natural language instruction for the refinement.
     * @param options - Optional request options.
     */
    async aiRefine(
      id: string,
      nodeId: string,
      instruction: string,
      options?: RequestOptions,
    ) {
      return rb.execute(
        patchAdminPipelineExecutionsByIdRefine,
        {
          path: { id },
          body: {
            data: {
              type: "pipeline-execution",
              id,
              attributes: { node_id: nodeId, instruction },
            },
          },
        },
        options,
      );
    },
  };
}
